import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  IconButton,
  Typography,
  Box,
} from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import InputText from "components/_refactor/text/input";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import UploadFile from "components/_refactor/dropzone";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import { columnTextField } from "utils/tables-utils";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import { MOCK_CATEGORIES } from "../constants";

const STATUS_OPTIONS = [
  { value: "ATIVO", label: "Ativo" },
  { value: "INATIVO", label: "Inativo" },
];

const EMPTY_FORM = {
  nome: "",
  descricao: "",
  status: STATUS_OPTIONS[0],
  ordem: "",
};

export default function DialogCategorias({ open, onClose }) {
  const snackbar = useSnackbar();
  const [categories, setCategories] = useState(MOCK_CATEGORIES);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [iconFiles, setIconFiles] = useState([]);

  useEffect(() => {
    if (!open) {
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
    }
  }, [open]);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setForm({
      nome: row.nome,
      descricao: row.descricao,
      status:
        STATUS_OPTIONS.find((s) => s.value === row.status) || STATUS_OPTIONS[0],
      ordem: String(row.ordem),
    });
    setShowForm(true);
  };

  const handleSave = () => {
    if (!form.nome?.trim()) {
      snackbar.showMessage("Nome da categoria é obrigatório.", {
        severity: "error",
      });
      return;
    }

    if (editingId) {
      setCategories((prev) =>
        prev.map((c) =>
          c.id === editingId
            ? {
                ...c,
                nome: form.nome,
                descricao: form.descricao,
                status: form.status?.value || "ATIVO",
                ordem: Number(form.ordem) || c.ordem,
              }
            : c,
        ),
      );
      snackbar.showMessage("Categoria atualizada com sucesso.", {
        severity: "success",
      });
    } else {
      setCategories((prev) => [
        ...prev,
        {
          id: Date.now(),
          nome: form.nome,
          descricao: form.descricao,
          status: form.status?.value || "ATIVO",
          ordem: Number(form.ordem) || prev.length + 1,
          cor: "#5D5FEF",
        },
      ]);
      snackbar.showMessage("Categoria criada com sucesso.", {
        severity: "success",
      });
    }

    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const columnModel = [
    columnTextField({
      dataField: "nome",
      text: "Categoria",
      align: "left",
      formatter: (_, row) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: row.cor,
              marginRight: 8,
              flexShrink: 0,
            }}
          />
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {row.nome}
          </Typography>
        </Box>
      ),
    }),
    columnTextField({
      dataField: "descricao",
      text: "Descrição",
      align: "left",
    }),
    columnTextField({
      dataField: "status",
      text: "Status",
      align: "center",
      formatter: (_, row) => (
        <Box
          style={{
            display: "inline-block",
            padding: "4px 10px",
            borderRadius: 6,
            backgroundColor: row.status === "ATIVO" ? "#0AA83633" : "#f5f5f5",
            color: row.status === "ATIVO" ? "#0AA836" : "#616161",
            fontSize: "0.75rem",
            fontWeight: 600,
          }}
        >
          {row.status === "ATIVO" ? "Ativo" : "Inativo"}
        </Box>
      ),
    }),
    columnTextField({ dataField: "ordem", text: "Ordem", align: "center" }),
    columnTextField({
      dataField: "acoes",
      text: "Ações",
      align: "center",
      formatter: (_, row) => (
        <IconButton
          size="small"
          onClick={() => handleEdit(row)}
          aria-label="Editar"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      ),
    }),
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Typography
          variant="h4"
          component="span"
          style={{ color: themeColorsDesingSystem.text.primary }}
        >
          Gestão de Categorias
        </Typography>
        <IconButton
          onClick={onClose}
          style={{ position: "absolute", right: 8, top: 8 }}
          aria-label="Fechar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {!showForm ? (
          <>
            <Box display="flex" justifyContent="flex-end" marginBottom={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={() => {
                  setEditingId(null);
                  setForm(EMPTY_FORM);
                  setShowForm(true);
                }}
              >
                Nova categoria
              </Button>
            </Box>
            <ToolkitTable
              keyField="id"
              dataTable={categories}
              columnModel={columnModel}
              notFoundTitle="Nenhuma categoria cadastrada."
              pagination={{
                options: [10, 25],
                defaultOption: 10,
                showTotal: true,
              }}
            />
          </>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                variant="h4"
                style={{ color: themeColorsDesingSystem.text.primary }}
              >
                {editingId ? "Editar Categoria" : "Nova Categoria"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <InputText
                label="Nome da categoria"
                value={form.nome}
                onChange={(e) => updateForm("nome", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <InputText
                label="Descrição"
                value={form.descricao}
                onChange={(e) => updateForm("descricao", e.target.value)}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="caption"
                style={{ color: themeColorsDesingSystem.gray.dark }}
              >
                Ícone / Cor
              </Typography>
              <UploadFile
                message="Upload de ícone (opcional)"
                accept={{ "image/*": [".svg", ".png", ".jpg"] }}
                onChangeFiles={setIconFiles}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <AutoCompleteField
                    label="Status"
                    value={form.status}
                    onChange={(v) => updateForm("status", v)}
                    options={STATUS_OPTIONS}
                    optionLabelKey="label"
                    optionIdKey="value"
                  />
                </Grid>
                <Grid item xs={12}>
                  <InputText
                    label="Ordem"
                    type="number"
                    value={form.ordem}
                    onChange={(e) => updateForm("ordem", e.target.value)}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        {showForm ? (
          <>
            <Button
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
                setForm(EMPTY_FORM);
              }}
              variant="outlined"
            >
              Voltar
            </Button>
            <Button onClick={handleSave} variant="contained" color="primary">
              Salvar categoria
            </Button>
          </>
        ) : (
          <Button onClick={onClose} variant="outlined">
            Fechar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
